import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Lock, ShieldCheck, Zap, FileSpreadsheet, Users, Calendar } from "lucide-react";

/**
 * Advanced Reporting & Year-End Prep
 * -----------------------------------
 * A consolidated admin dashboard that pulls together the pieces already living
 * across Reports.jsx (summary, tax-liability CSV, quarterly PDF) and Billing.jsx
 * (W-2 bulk purchase) into a single year-scoped view. Every call routes through
 * existing endpoints — no new backend logic is introduced here:
 *   • GET  /reports/summary
 *   • GET  /reports/tax-liability.csv?year=<year>
 *   • GET  /reports/quarterly-tax.pdf?year=<year>&quarter=<Q>
 *   • GET  /tax-documents                     (all pay-stub-derived W-2 rows)
 *   • GET  /tax-documents/w2/<year>/<emp>/pdf (per-employee W-2 PDF)
 *   • POST /billing/charge/w2-bulk            (Stripe checkout for the whole year)
 */
const QUARTERS = [1, 2, 3, 4];
const currentYear = new Date().getFullYear();
// Selectable range: current year down to 5 years back (typical W-2 retention window)
const YEARS = Array.from({ length: 6 }, (_, i) => String(currentYear - i));

export default function YearEnd() {
  const [year, setYear] = useState(String(currentYear));
  const [summary, setSummary] = useState(null);
  const [pro, setPro] = useState({ active: false });
  const [taxDocs, setTaxDocs] = useState([]);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [rs, bs, td] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/billing/status").catch(() => ({ data: { pro: { active: false } } })),
        api.get("/tax-documents"),
      ]);
      setSummary(rs.data);
      setPro(bs.data?.pro || { active: false });
      setTaxDocs(td.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle Stripe checkout return (bulk W-2 purchase)
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("cancelled")) toast.error("W-2 purchase cancelled");
    if (sid) { toast.success("W-2 bulk purchase confirmed"); params.delete("session_id"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yearDocs = useMemo(
    () => taxDocs.filter((d) => d.year === year),
    [taxDocs, year],
  );

  // Aggregate the selected year's totals so admins see the annual picture
  // without leaving the page. The full YTD from /reports/summary always sits
  // in the summary cards above; this block is scoped to the year selector.
  const yearTotals = useMemo(() => {
    const acc = { gross: 0, federal_tax: 0, social_security: 0, medicare: 0, state_tax: 0, employees: yearDocs.length };
    for (const d of yearDocs) {
      acc.gross += d.gross || 0;
      acc.federal_tax += d.federal_tax || 0;
      acc.social_security += d.social_security || 0;
      acc.medicare += d.medicare || 0;
      acc.state_tax += d.state_tax || 0;
    }
    return acc;
  }, [yearDocs]);

  const yearTotalTaxes = yearTotals.federal_tax + yearTotals.social_security + yearTotals.medicare + yearTotals.state_tax;
  const taxLiabilityUrl = `${API}/reports/tax-liability.csv?year=${year}`;
  const proActive = !!(pro && pro.active);

  const downloadQuarterlyPdf = async () => {
    setBusy("quarterly");
    try {
      const res = await api.get(`/reports/quarterly-tax.pdf?year=${year}&quarter=${quarter}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `quarterly-tax-Q${quarter}-${year}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Q${quarter} ${year} report downloaded`);
    } catch (e) {
      const detail = formatApiError(e);
      toast.error(detail.includes("Professional") ? "Professional plan required — see Billing" : detail);
    } finally { setBusy(null); }
  };

  const chargeW2Bulk = async () => {
    setBusy("w2-bulk");
    try {
      const { data } = await api.post("/billing/charge/w2-bulk", { year, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };

  if (!summary) return <div className="p-10 font-mono">Loading…</div>;

  const ytdTotalTaxes = (summary.ytd.federal_tax || 0) + (summary.ytd.social_security || 0) + (summary.ytd.medicare || 0) + (summary.ytd.state_tax || 0);

  return (
    <div className="p-10">
      <div className="overline mb-2">§ REPORTS &gt; YEAR-END PREP</div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-heading font-black text-5xl tracking-tight" data-testid="year-end-title">Advanced Reporting &amp; Year-End Prep</h1>
        <div className="flex items-center gap-3">
          <label className="overline text-[10px]">Tax Year</label>
          <select
            data-testid="year-selector"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border-2 border-ink px-4 py-2 font-mono font-black text-xl min-w-32"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* YTD SUMMARY (from /reports/summary) */}
      <div className="grid grid-cols-3 gap-6 mb-8" data-testid="ytd-summary">
        <Card label="YTD Gross" val={summary.ytd.gross} />
        <Card label="YTD Employer + Employee Taxes" val={ytdTotalTaxes} color="text-bad" />
        <Card label="YTD Net Disbursed" val={summary.ytd.net_pay} color="text-good" />
      </div>

      {/* SELECTED YEAR TOTALS (from /tax-documents) */}
      <div className="hard-card p-6 mb-8" data-testid="year-totals-card">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div className="overline flex items-center gap-2"><Calendar className="w-4 h-4" /> {year} TAX YEAR · SNAPSHOT</div>
          <div className="font-mono text-[10px] text-muted-foreground">{yearTotals.employees} employee{yearTotals.employees === 1 ? "" : "s"} · aggregated from every pay stub in {year}</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MiniStat label="Gross Wages" val={yearTotals.gross} />
          <MiniStat label="Federal Tax" val={yearTotals.federal_tax} />
          <MiniStat label="Social Security" val={yearTotals.social_security} />
          <MiniStat label="Medicare" val={yearTotals.medicare} />
          <MiniStat label="State Tax" val={yearTotals.state_tax} color="text-bad" />
        </div>
        <div className="mt-4 flex items-baseline justify-between border-t-2 border-ink pt-3">
          <span className="font-mono font-black text-sm">TOTAL {year} TAX LIABILITY</span>
          <span className="font-mono font-black text-xl">{fmtMoney(yearTotalTaxes)}</span>
        </div>
      </div>

      {/* COMPLIANCE — Quarterly PDF + Tax Liability CSV */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="hard-card p-6" data-testid="quarterly-card">
          <div className="flex items-start justify-between mb-3">
            <div className="overline flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> QUARTERLY TAX SUMMARY (PDF)</div>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${proActive ? "bg-good text-white" : "bg-warn text-ink"}`}>
              {proActive ? "Professional" : "Pro required"}
            </span>
          </div>
          <p className="font-mono text-xs text-muted-foreground mb-4 leading-relaxed">
            Downloadable PDF aggregating gross wages, federal / state withholding, SS &amp; Medicare, and net disbursed for the selected quarter — plus a per-employee breakdown. Perfect for filing 941s.
          </p>
          <div className="grid grid-cols-2 gap-2 items-end mb-3">
            <div>
              <div className="overline text-[10px] mb-1">Quarter</div>
              <select
                data-testid="quarter-select"
                value={quarter}
                onChange={(e) => setQuarter(Number(e.target.value))}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm"
              >
                {QUARTERS.map((q) => <option key={q} value={q}>Q{q}</option>)}
              </select>
            </div>
            <div className="text-right font-mono text-[10px] text-muted-foreground">Year is locked to selector above</div>
          </div>
          <button
            data-testid="download-quarterly-pdf"
            onClick={downloadQuarterlyPdf}
            disabled={busy === "quarterly" || !proActive}
            className="w-full hard-btn py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {!proActive && <Lock className="w-3.5 h-3.5" />}
            {proActive && <Download className="w-4 h-4" />}
            {busy === "quarterly" ? "Generating…" : `Download Q${quarter} ${year} PDF`}
          </button>
        </div>

        <div className="hard-card p-6" data-testid="tax-liability-card">
          <div className="overline mb-3 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> TAX LIABILITY CSV</div>
          <p className="font-mono text-xs text-muted-foreground mb-4 leading-relaxed">
            Line-by-line CSV of every pay stub in {year} — gross, each tax component, and net. Hand this to your CPA when filing quarterly 941s or year-end 940s.
          </p>
          <a
            data-testid="download-tax-liability-csv"
            href={taxLiabilityUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full hard-btn py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" /> Download {year} Tax Liability CSV
          </a>
        </div>
      </div>

      {/* W-2 LISTING + BULK GENERATE */}
      <div className="hard-card overflow-hidden mb-8" data-testid="w2-card">
        <div className="p-6 border-b-2 border-ink flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> § FORM W-2 · {year}</div>
            <div className="font-heading font-black text-2xl tracking-tight">Employee W-2s</div>
            <div className="font-mono text-xs text-muted-foreground mt-1 max-w-lg">Per-employee W-2 rows aggregated from every pay stub in {year}. Individual PDFs are free — the bulk file/mail service is $2.00 per employee.</div>
          </div>
          <button
            data-testid="w2-bulk-btn"
            onClick={chargeW2Bulk}
            disabled={busy === "w2-bulk" || yearDocs.length === 0}
            className="hard-btn px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="w-4 h-4" /> {busy === "w2-bulk" ? "Redirecting…" : `Generate ${year} W-2 Batch (${yearDocs.length} · $${(yearDocs.length * 2).toFixed(2)})`}
          </button>
        </div>
        {yearDocs.length === 0 ? (
          <div className="p-10 text-center font-mono text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Users className="w-6 h-6 text-ink/40" />
            No pay stubs found for {year} — W-2s are generated from historical stubs.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-sm" data-testid="w2-table">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Employee</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Stubs</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Gross</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Fed Tax</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">SS</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Medicare</th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">State Tax</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {yearDocs.map((d, i) => (
                  <tr key={`${d.employee_id}-${d.year}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`w2-row-${d.employee_id}`}>
                    <td className="px-4 py-3 border-t border-ink/20 font-semibold">{d.employee_name}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{d.stub_count}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{fmtMoney(d.gross)}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{fmtMoney(d.federal_tax)}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{fmtMoney(d.social_security)}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{fmtMoney(d.medicare)}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right tabular-nums">{fmtMoney(d.state_tax)}</td>
                    <td className="px-4 py-3 border-t border-ink/20 text-right">
                      <a
                        data-testid={`w2-download-${d.employee_id}`}
                        href={`${API}/tax-documents/w2/${d.year}/${d.employee_id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 border-2 border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white"
                      ><Download className="w-3 h-3" /> PDF</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, val, color = "" }) {
  return (
    <div className="hard-card p-6">
      <div className="overline mb-2">{label}</div>
      <div className={`font-mono font-black text-3xl ${color}`}>{fmtMoney(val)}</div>
    </div>
  );
}

function MiniStat({ label, val, color = "" }) {
  return (
    <div>
      <div className="overline mb-1 text-[9px]">{label}</div>
      <div className={`font-mono font-black text-lg ${color}`}>{fmtMoney(val)}</div>
    </div>
  );
}
